/**
 * ============================================================================
 * STORAGE HELPERS (Shared Utilities)
 * ============================================================================
 *
 * Shared serialization, compression, and validation utilities
 * used by both ViewState storage and Tab metadata storage.
 */

import dayjs from 'dayjs';
import LZString from 'lz-string';

import {
  AlertBoardView,
  AlertBoardPreviewState,
  TabbedView,
  TimeSelection,
  ViewState,
  ViewType,
} from '@controlrooms/models';

const COMPRESSION_THRESHOLD = 100 * 1024; // 100KB

/**
 * Convert a date-like value (Date, dayjs, or ISO string) to an ISO string.
 * ViewState timeSelection may have dayjs instances that don't have .toISOString() in all builds.
 */
function toISOString(val: unknown): string {
  if (val == null) return '';
  if (typeof val === 'string') return val;
  if (typeof (val as { toISOString?: () => string }).toISOString === 'function') {
    return (val as { toISOString: () => string }).toISOString();
  }
  const d = dayjs(val as string | number | Date);
  return d.isValid() ? d.toISOString?.() ?? d.format('YYYY-MM-DDTHH:mm:ss.SSS[Z]') : '';
}

/**
 * ============================================================================
 * SERIALIZATION
 * ============================================================================
 */

/**
 * Serialize TimeSelection by converting dayjs/Date objects to ISO strings
 */
export function serializeTimeSelection(timeSelection: TimeSelection): Omit<
  TimeSelection,
  'startTime' | 'endTime'
> & {
  startTime: string;
  endTime: string;
} {
  return {
    ...timeSelection,
    startTime: toISOString(timeSelection.startTime),
    endTime: toISOString(timeSelection.endTime),
  };
}

/**
 * Deserialize TimeSelection by converting ISO strings back to dayjs objects
 */
export function deserializeTimeSelection(
  serialized: Omit<TimeSelection, 'startTime' | 'endTime'> & {
    startTime: string;
    endTime: string;
  },
): TimeSelection {
  return {
    ...serialized,
    startTime: dayjs(serialized.startTime),
    endTime: dayjs(serialized.endTime),
  };
}

/** Serialized TimeSelection (ISO string dates for storage) */
type SerializedTimeSelectionShape = Omit<TimeSelection, 'startTime' | 'endTime'> & {
  startTime: string;
  endTime: string;
};

/** Serialized alert board preview (string dates) for viewState.view.alert_board.preview */
type SerializedAlertBoardPreviewState = {
  timeSelection?: SerializedTimeSelectionShape | null;
  timeSelectionHistory?: SerializedTimeSelectionShape[];
};

/**
 * Serialized ViewState type (with string dates instead of dayjs)
 */
export type SerializedViewState = Omit<
  ViewState,
  'timeSelection' | 'timeSelectionHistory' | 'view'
> & {
  timeSelection: SerializedTimeSelectionShape;
  timeSelectionHistory: SerializedTimeSelectionShape[];
  view: Omit<TabbedView, ViewType.ALERT_BOARD> & {
    [ViewType.ALERT_BOARD]?: Omit<AlertBoardView, 'preview'> & {
      preview?: SerializedAlertBoardPreviewState;
    };
  };
};

/**
 * Serialize ViewState for storage (convert dayjs to ISO strings)
 */
export function serializeViewState(viewState: ViewState): SerializedViewState {
  const alertBoard = viewState.view?.[ViewType.ALERT_BOARD];
  const preview = alertBoard?.preview;
  const serializedPreview =
    preview && (preview.timeSelection || (preview.timeSelectionHistory?.length ?? 0) > 0)
      ? {
          timeSelection: preview.timeSelection
            ? serializeTimeSelection(preview.timeSelection)
            : undefined,
          timeSelectionHistory: (preview.timeSelectionHistory ?? []).map(serializeTimeSelection),
        }
      : undefined;

  return {
    ...viewState,
    timeSelection: serializeTimeSelection(viewState.timeSelection),
    timeSelectionHistory: viewState.timeSelectionHistory.map(serializeTimeSelection),
    view: viewState.view
      ? {
          ...viewState.view,
          [ViewType.ALERT_BOARD]: {
            ...alertBoard,
            ...(serializedPreview && { preview: serializedPreview }),
          },
        }
      : viewState.view,
  } as SerializedViewState;
}

type SerializedTimeSelection = Omit<TimeSelection, 'startTime' | 'endTime'> & {
  startTime: string;
  endTime: string;
};

function isSerializedTimeSelection(v: unknown): v is SerializedTimeSelection {
  return (
    v != null &&
    typeof v === 'object' &&
    typeof (v as SerializedTimeSelection).startTime === 'string' &&
    typeof (v as SerializedTimeSelection).endTime === 'string'
  );
}

/**
 * Deserialize ViewState from storage (convert ISO strings back to dayjs)
 */
export function deserializeViewState(serialized: SerializedViewState): ViewState {
  const alertBoard = serialized.view?.[ViewType.ALERT_BOARD];
  const preview = alertBoard?.preview as
    | { timeSelection?: SerializedTimeSelection; timeSelectionHistory?: SerializedTimeSelection[] }
    | undefined;
  let deserializedPreview: AlertBoardPreviewState | undefined;
  if (preview) {
    deserializedPreview = {
      timeSelection: isSerializedTimeSelection(preview.timeSelection)
        ? deserializeTimeSelection(preview.timeSelection)
        : undefined,
      timeSelectionHistory: Array.isArray(preview.timeSelectionHistory)
        ? preview.timeSelectionHistory
            .filter(isSerializedTimeSelection)
            .map(deserializeTimeSelection)
        : [],
    };
  }

  return {
    ...serialized,
    timeSelection: deserializeTimeSelection(serialized.timeSelection),
    timeSelectionHistory: serialized.timeSelectionHistory.map(deserializeTimeSelection),
    view: serialized.view
      ? {
          ...serialized.view,
          [ViewType.ALERT_BOARD]: {
            ...alertBoard,
            ...(deserializedPreview && { preview: deserializedPreview }),
          },
        }
      : serialized.view,
  } as ViewState;
}

/**
 * ============================================================================
 * COMPRESSION
 * ============================================================================
 */

/**
 * Calculate the size of a string in bytes
 */
function calculateSize(str: string): number {
  return new Blob([str]).size;
}

/**
 * Compress ViewState if it exceeds the threshold
 */
export function compressViewState(viewState: ViewState): string {
  const serialized = serializeViewState(viewState);
  const json = JSON.stringify(serialized);
  const size = calculateSize(json);

  if (size > COMPRESSION_THRESHOLD) {
    const compressed = LZString.compress(json);
    if (compressed) {
      return `compressed:${compressed}`;
    }
  }

  return json;
}

/**
 * Decompress ViewState if it's compressed
 */
export function decompressViewState(compressed: string): string {
  if (compressed.startsWith('compressed:')) {
    const decompressed = LZString.decompress(compressed.substring(11));
    if (!decompressed) {
      throw new Error('Failed to decompress ViewState');
    }
    return decompressed;
  }
  return compressed;
}

/**
 * ============================================================================
 * VALIDATION
 * ============================================================================
 */

/**
 * Validate ViewState structure
 */
export function validateViewState(viewState: unknown): viewState is ViewState {
  if (!viewState || typeof viewState !== 'object') {
    return false;
  }

  const state = viewState as Record<string, unknown>;

  return (
    typeof state.viewId === 'string' &&
    typeof state.selectedMode === 'string' &&
    typeof state.name === 'string' &&
    typeof state.tenant_id === 'number' &&
    state.view !== undefined &&
    state.timeSelection !== undefined &&
    state.config !== undefined
  );
}
